import React, { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, Mail } from "lucide-react";
import fr from "@/content/fr";
import { trackLead } from "@/lib/analytics";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[+]?[()\d\s.\-]{7,}$/;
const URL_RE = /^(https?:\/\/|www\.|[a-z0-9.-]+\.[a-z]{2,})/i;

const FieldLabel = ({ htmlFor, children }) => (
  <label
    htmlFor={htmlFor}
    className="block text-[11px] tracking-[0.22em] uppercase text-white/60 font-mono mb-2"
  >
    {children}
  </label>
);

const baseInput =
  "w-full h-12 px-4 rounded-xl bg-white/[0.04] border border-white/[0.10] text-white placeholder:text-white/35 outline-none transition focus:bg-white/[0.06] focus:border-white/35 focus-visible:ring-2 focus-visible:ring-[#E91E63]/50";

/**
 * <ApplicationForm />
 *
 * Generic candidature/application form, shared by every "résidence" tab
 * (Streetwear, Beauty…). Same field set and validation as the homepage
 * <PartnerForm />, but with a page-specific category list and a `program`
 * tag sent to `contact.php` so submissions can be told apart in the inbox.
 *
 * Props:
 *   - idPrefix: string — unique prefix for field ids / data-testid (e.g. "streetwear")
 *   - programLabel: string — human label sent as the "program" field (e.g. "Creators Market™")
 *   - categoryOptions: string[] — options for the category select
 */
export const ApplicationForm = ({ idPrefix, programLabel, categoryOptions }) => {
  const [values, setValues] = useState({
    company: "",
    contact: "",
    email: "",
    phone: "",
    link: "",
    category: "",
    // honeypot — must remain empty
    website_url: "",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState("idle"); // 'idle' | 'submitting' | 'success' | 'error'

  const setField = (k, v) => {
    setValues((s) => ({ ...s, [k]: v }));
    if (touched[k]) {
      setErrors((er) => ({ ...er, [k]: validateField(k, v) }));
    }
  };

  const validateField = (k, v) => {
    const required = ["company", "contact", "email", "phone", "category"];
    if (required.includes(k) && !String(v).trim()) return fr.partners.errors.required;
    if (k === "email" && !EMAIL_RE.test(v)) return fr.partners.errors.email;
    if (k === "phone" && !PHONE_RE.test(v)) return fr.partners.errors.phone;
    if (k === "link" && v && !URL_RE.test(v)) return fr.partners.errors.url;
    return "";
  };

  const validateAll = () => {
    const next = {};
    ["company", "contact", "email", "phone", "link", "category"].forEach((k) => {
      const e = validateField(k, values[k]);
      if (e) next[k] = e;
    });
    return next;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    // Honeypot — bots will fill this; humans never see it.
    if (values.website_url) return;

    const errs = validateAll();
    setErrors(errs);
    setTouched({
      company: true,
      contact: true,
      email: true,
      phone: true,
      link: true,
      category: true,
    });
    if (Object.keys(errs).length > 0) return;

    setStatus("submitting");
    try {
      const formData = new FormData();
      Object.entries(values).forEach(([k, v]) => formData.append(k, v));
      formData.append("program", programLabel);

      const res = await fetch("/contact.php", {
        method: "POST",
        body: formData,
      });

      let data = null;
      try {
        data = await res.json();
      } catch {
        data = null;
      }

      if (!res.ok || (data && data.ok === false)) {
        throw new Error((data && data.error) || "send_failed");
      }

      trackLead({ content_name: `application_${idPrefix}`, category: values.category });
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  const reset = () => {
    setValues({
      company: "",
      contact: "",
      email: "",
      phone: "",
      link: "",
      category: "",
      website_url: "",
    });
    setErrors({});
    setTouched({});
    setStatus("idle");
  };

  return (
    <motion.div
      initial={{ y: 30, opacity: 0 }}
      whileInView={{ y: 0, opacity: 1 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.8 }}
      className="mt-14 rounded-3xl p-6 sm:p-10 lg:p-12 bg-white/[0.04] backdrop-blur-xl border border-white/[0.10] shadow-[0_40px_120px_-40px_rgba(0,0,0,0.7)]"
      data-testid={`${idPrefix}-application-card`}
    >
      {status === "success" ? (
        <div
          className="text-center py-10"
          data-testid={`${idPrefix}-application-success`}
          role="status"
          aria-live="polite"
        >
          <div className="mx-auto w-16 h-16 rounded-full bg-[#E91E63]/15 border border-[#E91E63]/40 flex items-center justify-center">
            <CheckCircle2 className="text-[#E91E63]" size={28} />
          </div>
          <h3 className="mt-6 font-display font-extrabold text-white text-3xl uppercase tracking-tight">
            {fr.partners.successTitle}
          </h3>
          <p className="mt-4 text-white/72 max-w-[480px] mx-auto leading-[1.65]">
            {fr.partners.successBody}
          </p>
          <button
            type="button"
            onClick={reset}
            data-testid={`${idPrefix}-application-reset`}
            className="mt-7 inline-flex items-center justify-center h-11 px-6 rounded-full border border-white/20 text-white/85 hover:text-white hover:border-white/50 transition uppercase tracking-[0.16em] text-xs font-display font-bold"
          >
            {fr.partners.successReset}
          </button>
        </div>
      ) : (
        <form
          onSubmit={onSubmit}
          noValidate
          data-testid={`${idPrefix}-application-form`}
          className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6"
        >
          {/* Honeypot — visually hidden, not for users */}
          <div aria-hidden className="absolute -left-[9999px] w-px h-px overflow-hidden">
            <label>
              Website URL
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={values.website_url}
                onChange={(e) => setField("website_url", e.target.value)}
              />
            </label>
          </div>

          {/* company */}
          <div>
            <FieldLabel htmlFor={`${idPrefix}-company`}>{fr.partners.fields.company.label} *</FieldLabel>
            <input
              id={`${idPrefix}-company`}
              data-testid={`${idPrefix}-input-company`}
              type="text"
              required
              className={baseInput}
              placeholder={fr.partners.fields.company.placeholder}
              value={values.company}
              onChange={(e) => setField("company", e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, company: true }))}
              aria-invalid={!!errors.company}
            />
            {errors.company && <p className="mt-1.5 text-[12px] text-[#ff8fb1]">{errors.company}</p>}
          </div>

          {/* contact */}
          <div>
            <FieldLabel htmlFor={`${idPrefix}-contact`}>{fr.partners.fields.contact.label} *</FieldLabel>
            <input
              id={`${idPrefix}-contact`}
              data-testid={`${idPrefix}-input-contact`}
              type="text"
              required
              className={baseInput}
              placeholder={fr.partners.fields.contact.placeholder}
              value={values.contact}
              onChange={(e) => setField("contact", e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, contact: true }))}
              aria-invalid={!!errors.contact}
            />
            {errors.contact && <p className="mt-1.5 text-[12px] text-[#ff8fb1]">{errors.contact}</p>}
          </div>

          {/* email */}
          <div>
            <FieldLabel htmlFor={`${idPrefix}-email`}>{fr.partners.fields.email.label} *</FieldLabel>
            <input
              id={`${idPrefix}-email`}
              data-testid={`${idPrefix}-input-email`}
              type="email"
              required
              className={baseInput}
              placeholder={fr.partners.fields.email.placeholder}
              value={values.email}
              onChange={(e) => setField("email", e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, email: true }))}
              aria-invalid={!!errors.email}
            />
            {errors.email && <p className="mt-1.5 text-[12px] text-[#ff8fb1]">{errors.email}</p>}
          </div>

          {/* phone */}
          <div>
            <FieldLabel htmlFor={`${idPrefix}-phone`}>{fr.partners.fields.phone.label} *</FieldLabel>
            <input
              id={`${idPrefix}-phone`}
              data-testid={`${idPrefix}-input-phone`}
              type="tel"
              required
              className={baseInput}
              placeholder={fr.partners.fields.phone.placeholder}
              value={values.phone}
              onChange={(e) => setField("phone", e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, phone: true }))}
              aria-invalid={!!errors.phone}
            />
            {errors.phone && <p className="mt-1.5 text-[12px] text-[#ff8fb1]">{errors.phone}</p>}
          </div>

          {/* link (optional) */}
          <div className="sm:col-span-2">
            <FieldLabel htmlFor={`${idPrefix}-link`}>{fr.partners.fields.link.label}</FieldLabel>
            <input
              id={`${idPrefix}-link`}
              data-testid={`${idPrefix}-input-link`}
              type="text"
              className={baseInput}
              placeholder={fr.partners.fields.link.placeholder}
              value={values.link}
              onChange={(e) => setField("link", e.target.value)}
              onBlur={() => setTouched((t) => ({ ...t, link: true }))}
              aria-invalid={!!errors.link}
            />
            {errors.link && <p className="mt-1.5 text-[12px] text-[#ff8fb1]">{errors.link}</p>}
          </div>

          {/* category */}
          <div className="sm:col-span-2">
            <FieldLabel htmlFor={`${idPrefix}-category`}>{fr.partners.fields.category.label} *</FieldLabel>
            <div className="relative">
              <select
                id={`${idPrefix}-category`}
                data-testid={`${idPrefix}-input-category`}
                required
                className={`${baseInput} appearance-none pr-12 ${values.category ? "text-white" : "text-white/35"}`}
                value={values.category}
                onChange={(e) => setField("category", e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, category: true }))}
                aria-invalid={!!errors.category}
              >
                <option value="" disabled>
                  {fr.partners.fields.category.placeholder}
                </option>
                {categoryOptions.map((opt) => (
                  <option key={opt} value={opt} className="bg-[#15042E] text-white">
                    {opt}
                  </option>
                ))}
              </select>
              <svg
                aria-hidden
                viewBox="0 0 20 20"
                className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/55 pointer-events-none"
              >
                <path d="M5 8l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            {errors.category && <p className="mt-1.5 text-[12px] text-[#ff8fb1]">{errors.category}</p>}
          </div>

          {/* submit row */}
          <div className="sm:col-span-2 mt-2 flex flex-col sm:flex-row sm:items-center gap-4">
            <button
              type="submit"
              disabled={status === "submitting"}
              data-testid={`${idPrefix}-submit`}
              className="group inline-flex items-center justify-center gap-3 h-14 px-7 rounded-full bg-[#E91E63] hover:bg-[#ff2974] text-white font-display font-extrabold uppercase tracking-[0.12em] text-[15px] shadow-[0_0_0_0_rgba(233,30,99,0.0)] hover:shadow-[0_0_44px_4px_rgba(233,30,99,0.55)] transition-all duration-300 hover:-translate-y-[2px] disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              {status === "submitting" ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  {fr.partners.submitting}
                </>
              ) : (
                fr.partners.submit
              )}
            </button>

            <p className="text-[12px] text-white/45 flex items-center gap-2">
              <Mail size={14} /> Réponse garantie sous 48 h.
            </p>

            {status === "error" && (
              <p role="alert" className="text-[13px] text-[#ff8fb1] sm:ml-auto" data-testid={`${idPrefix}-error-generic`}>
                {fr.partners.errorGeneric}
              </p>
            )}
          </div>
        </form>
      )}
    </motion.div>
  );
};

export default ApplicationForm;
